import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Globe, Plus, X } from 'lucide-react';
import { ANY_GEO, ISO_GEO_CODES, flagUrl, geoName, isIsoGeo, normalizeGeoList, parseStoredGeo } from '../lib/geo';
import { GeoChip } from './GeoFlags';

type Row =
  | { kind: 'any' }
  | { kind: 'code'; code: string }
  | { kind: 'create'; code: string };

/**
 * Inline multi-select for a domain's target geos. Pick one or more countries,
 * or "Any geo" (exclusive — it clears the countries, and picking a country
 * clears it). Non-ISO labels already in use (e.g. "LATAM") are offered too;
 * creating a new one is admin-only (`canCreate`), as on the server.
 */
export default function GeoMultiSelect({
  value,
  options,
  canCreate,
  saving,
  onCommit,
  onCancel,
}: {
  value: string[] | null;
  /** Codes already in use across the inventory (from filter-options). */
  options: string[];
  canCreate: boolean;
  saving?: boolean;
  /** Called with the canonical list (["AU","GB"] / ["ANY"]); [] clears it. */
  onCommit: (codes: string[]) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(() => parseStoredGeo(value));
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const allCodes = useMemo(() => {
    const custom = options.filter((o) => o !== ANY_GEO && !isIsoGeo(o));
    return [...ISO_GEO_CODES, ...custom];
  }, [options]);

  const rows: Row[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Codes in use rank above the rest of the world, then by name.
    const inUse = new Set(options);
    const matches = allCodes
      .filter((c) => !q || c.toLowerCase().includes(q) || geoName(c).toLowerCase().includes(q) || (c === 'GB' && 'uk'.includes(q)))
      .sort((a, b) => {
        const qa = q && (a.toLowerCase() === q || geoName(a).toLowerCase().startsWith(q)) ? 0 : 1;
        const qb = q && (b.toLowerCase() === q || geoName(b).toLowerCase().startsWith(q)) ? 0 : 1;
        const ua = inUse.has(a) ? 0 : 1;
        const ub = inUse.has(b) ? 0 : 1;
        return qa - qb || ua - ub || geoName(a).localeCompare(geoName(b));
      })
      .map((code): Row => ({ kind: 'code', code }));
    const out: Row[] = [];
    if (!q || 'any geo all'.includes(q) || q === 'all geos') out.push({ kind: 'any' });
    out.push(...matches);
    const typed = query.trim().replace(/\s+/g, ' ').toUpperCase();
    if (typed && canCreate && matches.length === 0 && typed !== ANY_GEO) out.push({ kind: 'create', code: typed });
    return out;
  }, [query, allCodes, options, canCreate]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) onCancel();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onCancel]);

  const toggle = (row: Row | undefined) => {
    if (!row || saving) return;
    if (row.kind === 'any') {
      setSelected((s) => (s.includes(ANY_GEO) ? [] : [ANY_GEO]));
    } else {
      const code = row.code;
      setSelected((s) => {
        const without = s.filter((c) => c !== ANY_GEO);
        return without.includes(code) ? without.filter((c) => c !== code) : [...without, code];
      });
    }
    setQuery('');
    inputRef.current?.focus();
  };

  const save = () => {
    if (saving) return;
    onCommit(normalizeGeoList(selected));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      // Enter toggles the highlighted row; Shift/Cmd/Ctrl+Enter saves.
      if (e.shiftKey || e.metaKey || e.ctrlKey) save();
      else toggle(rows[active]);
    }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
    else if (e.key === 'Backspace' && !query && selected.length) setSelected((s) => s.slice(0, -1));
  };

  const isAny = selected.includes(ANY_GEO);

  return (
    <div ref={boxRef} className="relative flex-1">
      <div className="flex items-start gap-1.5">
        <div
          className="input-sm flex-1 flex flex-wrap items-center gap-1 min-h-[2rem] py-1 cursor-text"
          onClick={() => inputRef.current?.focus()}
        >
          {selected.map((c) => (
            <span key={c} className="inline-flex items-center">
              <GeoChip code={c} />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setSelected((s) => s.filter((x) => x !== c)); }}
                className="ml-0.5 text-slate-400 hover:text-slate-600"
                title={`Remove ${geoName(c)}`}
                disabled={saving}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={selected.length ? 'Add…' : 'Search countries…'}
            className="flex-1 min-w-[6rem] border-0 p-0 text-sm focus:ring-0 focus:outline-none bg-transparent"
            disabled={saving}
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
          />
        </div>
        <button onClick={save} disabled={saving} className="btn btn-primary text-xs px-2 py-1" title="Save (Shift+Enter)">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} className="btn-ghost text-slate-400 hover:text-slate-600 mt-1" title="Cancel">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div
        ref={listRef}
        role="listbox"
        aria-multiselectable="true"
        className="absolute left-0 right-24 mt-1 z-20 max-h-64 overflow-y-auto bg-surface border border-slate-200 rounded-md shadow-lg py-1"
      >
        {rows.length === 0 && (
          <div className="px-3 py-2 text-xs text-slate-400">
            No matching country. {canCreate ? '' : 'Only admins can add other geo labels.'}
          </div>
        )}
        {rows.map((row, idx) => {
          const isActive = idx === active;
          const base = `w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${isActive ? 'bg-brand-50' : ''}`;
          if (row.kind === 'any') {
            return (
              <button key="__any" data-idx={idx} type="button" role="option" aria-selected={isAny}
                onMouseEnter={() => setActive(idx)} onClick={() => toggle(row)}
                className={`${base} border-b border-slate-100 ${isAny ? 'font-medium text-brand-700' : 'text-slate-700'}`}>
                <Globe className="h-3.5 w-4 text-slate-500 shrink-0" />
                <span className="flex-1">Any geo <span className="text-xs text-slate-400">— not targeted at specific countries</span></span>
                {isAny && <Check className="h-3.5 w-3.5 text-brand-600 shrink-0" />}
              </button>
            );
          }
          if (row.kind === 'create') {
            return (
              <button key="__create" data-idx={idx} type="button" role="option" aria-selected={false}
                onMouseEnter={() => setActive(idx)} onClick={() => toggle(row)}
                className={`${base} text-brand-700 border-t border-slate-100`}>
                <Plus className="h-3.5 w-3.5 shrink-0" /> Add geo label “{row.code}”
              </button>
            );
          }
          const on = selected.includes(row.code);
          const url = flagUrl(row.code);
          return (
            <button key={row.code} data-idx={idx} type="button" role="option" aria-selected={on}
              onMouseEnter={() => setActive(idx)} onClick={() => toggle(row)}
              className={`${base} ${on ? 'font-medium text-brand-700' : 'text-slate-700'}`}>
              {url
                ? <img src={url} alt="" loading="lazy" className="h-3 w-4 rounded-[2px] object-cover shrink-0 shadow-[0_0_0_0.5px_rgba(0,0,0,0.15)]" />
                : <Globe className="h-3.5 w-4 text-slate-400 shrink-0" />}
              <span className="flex-1 truncate">{geoName(row.code)}</span>
              <span className="text-xs text-slate-400 tabular-nums">{row.code}</span>
              {on && <Check className="h-3.5 w-3.5 text-brand-600 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
